<script lang="ts" setup>
import LightArrowIcon from "@/components/icon/LightArrowIcon.vue";
import {
  CellGroup,
  Tab,
  Tabs,
  showConfirmDialog,
  showLoadingToast,
} from "vant";
import { computed, onMounted, ref } from "vue";
import { ContentRatingEnum } from "@/enums";
import { ChatSceneScoreType, MateCardPromptType } from "@/enums/chatEnums";
import { useI18n } from "vue-i18n";
import {
  ChatScene,
  ChatScenePosition,
  ChatSceneScore,
  GenerateMateContext,
} from "@/types/chat";
import { useRouteParams, useRouteQuery } from "@vueuse/router";
import { useMate } from "@/stores/mate";
import { useChatScene } from "@/stores/chatScene";
import { deleteOneChatSceneEvent, updateOneChatScene } from "@/apis/chatScene";
import { createOneChatScenePosition } from "@/apis/chat";
import { Mate } from "@/types/entity";

const uuid = useRouteParams("uuid", "");
const chatSceneId = useRouteQuery("id", 0, {
  transform: Number,
});

const getDefaultChatScene = (): ChatScene => {
  return {
    mateId: 0,
    name: "",
    systemPrompt: "",
    scenario: "",
    greeting: "",
    isNsfw: ContentRatingEnum.SFW,
    tag: "",
    intro: "",
    mediaId: 0,
    isCommon: false,
    eventzz: [],
    scorezz: [],
  };
};
const { t } = useI18n();
const mateStore = useMate();
const chatSceneStore = useChatScene();

const targetMate = ref<Mate>();
const active = ref("basic");

const loading = ref(false);
const targetScene = ref<ChatScene>(getDefaultChatScene());

onMounted(async () => {
  try {
    loading.value = true;
    targetMate.value = await mateStore.getOrFetchMate(uuid.value);
    if (chatSceneId.value) {
      const chatScene = await chatSceneStore.getOrFetchChatScene(
        targetMate.value.id!,
        chatSceneId.value
      );
      const validChatScene = Object.fromEntries(
        Object.entries(chatScene!).filter(([_, v]) => v !== null)
      ) as ChatScene;
      validChatScene.setAsDefault =
        mateStore.currentMate?.defaultChatSceneId === chatSceneId.value;
      Object.assign(targetScene.value, validChatScene);
      targetScene.value.positionzz = handlePosition(validChatScene);
    } else {
      targetScene.value.mateId = targetMate.value.id!;
    }
  } finally {
    loading.value = false;
  }
});

const handlePosition = (chatScene: ChatScene) => {
  let positions: ChatScenePosition[] = [];

  // 1. 将positionzz中parentId为null的数据筛选出来，添加到 1 层
  const parentIdMap = new Map<number, ChatScenePosition>();
  chatScene.positionzz?.forEach((position) => {
    if (position.parentId === null) {
      position.positionzz = [];
      parentIdMap.set(position.id!, position);
      positions.push(position);
    }
  });

  // 2. 根据parentIdMap，将positionzz中parentId不为null的数据添加到对应的parent的positionzz中
  chatScene.positionzz?.forEach((position) => {
    if (position.parentId) {
      const parent = parentIdMap.get(position.parentId);
      if (parent) {
        if (!parent.positionzz) parent.positionzz = [];
        parent.positionzz?.push(position);
      }
    }
  });

  return positions;
};

const mateContext = computed<GenerateMateContext>(() => {
  return {
    bio: targetMate.value?.bio,
    age: targetMate.value?.age,
    gender: targetMate.value?.gender,
    scenario: targetScene.value.scenario,
    personalityPrompt: targetMate.value?.personalityPrompt,
  };
});

const clickAddEvent = () => {
  targetScene.value.eventzz!.push({
    chatSceneId: targetScene.value.id,
    name: "",
    probability: 0,
    definition: "",
  });
};

const sortEvent = (payload: { idx: number; direction: "up" | "down" }) => {
  const { idx, direction } = payload;
  const event = targetScene.value.eventzz![idx];
  targetScene.value.eventzz!.splice(idx, 1);
  targetScene.value.eventzz!.splice(
    direction === "up" ? idx - 1 : idx + 1,
    0,
    event
  );
};

const deleteEvent = (idx: number) => {
  showConfirmDialog({
    message: t("Message.deleteEventTips"),
    confirmButtonText: t("Buttons.confirm"),
    cancelButtonText: t("Buttons.cancel"),
  }).then(async () => {
    const event = targetScene.value.eventzz![idx];
    targetScene.value.eventzz!.splice(idx, 1);
    if (event.id) {
      await deleteOneChatSceneEvent(event.id);
    }
  });
};

const clickAddScore = () => {
  targetScene.value.scorezz!.push({
    chatSceneId: targetScene.value.id!,
    name: "",
    defaultScore: 0,
    definitionzz: [],
    scoringInstruction: "",
    type: ChatSceneScoreType.Status,
    characterzz: [],
  });
};

const updateScore = (score: ChatSceneScore, idx: number) => {
  targetScene.value.scorezz![idx] = score;
};

const saveChatScene = async (attr: string) => {
  chatSceneStore.withSaving(() =>
    updateOneChatScene(targetScene.value.id!, {
      [attr]: targetScene.value[attr as keyof ChatScene],
    })
  );
};

const clickAddPosition = () => {
  const loading = showLoadingToast(t("Message.loading"));
  createOneChatScenePosition({
    chatSceneId: targetScene.value.id!,
  }).then((res) => {
    res.item.positionzz = [];
    targetScene.value.positionzz!.push(res.item);
    loading.close();
  });
};
</script>
<template>
  <div class="h-full grid grid-rows-[auto_1fr]">
    <ThreadsNav :show-center="false" class="border-b border-base-content/10">
      <template #left>
        <button class="btn btn-ghost" @click="$router.back">
          <light-arrow-icon />
        </button>
        <div
          class="flex gap-1 text-xs md:text-sm items-center rounded-full border px-2 whitespace-nowrap py-1"
        >
          <div
            :class="[
              'rounded-full w-2 h-2',
              chatSceneStore.isSaving ? 'bg-warning' : 'bg-success',
            ]"
          />
          <span v-if="chatSceneStore.isSaving">
            {{ $t("User.scenarioSaving") }}</span
          >
          <span v-else>{{ $t("User.scenarioSaved") }}</span>
        </div>
      </template>
    </ThreadsNav>
    <div class="w-full h-full overflow-y-auto relative">
      <div
        v-if="loading"
        class="flex items-center justify-center w-full h-full"
      >
        <span class="loading" />
      </div>
      <Tabs v-else v-model:active="active" :offset-top="64" sticky>
        <Tab :title="$t('Chats.chatScene.edit.basic')" name="basic">
          <ChatSceneBasic
            v-if="targetScene"
            :target-scene="targetScene"
            @save="saveChatScene"
          />
        </Tab>
        <Tab :title="$t('Chats.chatScene.edit.numerical')" name="numerical">
          <div v-for="(item, idx) in targetScene.scorezz">
            <ChatSceneScoreItem
              :score="item"
              :chat-scene="targetScene"
              :idx="idx"
              :mate-context="mateContext"
              @update:score="updateScore"
            />
          </div>

          <div class="flex justify-center px-4 my-4">
            <button
              class="btn btn-sm btn-neutral text-base-content/50 gap-1"
              @click="clickAddScore"
            >
              <i class="iconify mingcute--add-line text-xs" />
              {{
                $t("Buttons.add") + " " + $t("Chats.chatScene.edit.numerical")
              }}
            </button>
          </div>
        </Tab>

        <Tab :title="$t('Chats.chatScene.edit.position')" name="position">
          <div v-for="(item, idx) in targetScene.positionzz">
            <ChatScenePositionItem
              :position="item"
              :chat-scene="targetScene"
              :idx="idx"
              :mate-context="mateContext"
            />
          </div>

          <div class="flex justify-center px-4 my-4">
            <button
              class="btn btn-sm btn-neutral text-base-content/50 gap-1"
              @click="clickAddPosition"
            >
              <i class="iconify mingcute--add-line text-xs" />
              {{
                $t("Buttons.add") + " " + $t("Chats.chatScene.edit.position")
              }}
            </button>
          </div>
        </Tab>
        <Tab :title="$t('Chats.chatScene.edit.desc')" name="desc">
          <div class="px-4 py-2 text-base-content/50">
            {{ $t("Chats.chatScene.edit.descTip") }}
          </div>
          <div class="px-4">
            <GenerableTextArea
              v-model:model-value="targetScene.scenario"
              :label="$t('User.mate.chat.scenario')"
              :mateContext="mateContext"
              :promptType="MateCardPromptType.Scenario"
              @blur="saveChatScene('scenario')"
            />
          </div>
        </Tab>
        <Tab :title="$t('Chats.chatScene.edit.firstMsg')" name="greeting">
          <div class="px-4 py-2 text-base-content/50 required-field">
            {{ $t("Chats.chatScene.edit.firstMsgTip") }}
          </div>
          <div class="px-4">
            <GenerableTextArea
              v-model="targetScene.greeting"
              :label="$t('User.mate.chat.greetingMessage')"
              :mateContext="mateContext"
              :promptType="MateCardPromptType.Greeting"
              @blur="saveChatScene('greeting')"
            />
          </div>
        </Tab>
        <Tab
          :title="$t('Chats.chatScene.edit.systemPrompt')"
          name="systemPrompt"
        >
          <div class="px-4 py-2 text-base-content/50">
            {{ $t("Chats.chatScene.edit.systemPromptTip") }}
          </div>
          <div class="px-4">
            <GenerableTextArea
              v-model="targetScene.systemPrompt"
              :enable-generate="false"
              :label="$t('User.mate.chat.systemPrompt')"
              :mateContext="mateContext"
              :promptType="MateCardPromptType.Greeting"
              @blur="saveChatScene('systemPrompt')"
            />
          </div>
        </Tab>
        <Tab
          :title="$t('Chats.chatScene.edit.target')"
          name="target"
          class="pb-5"
        >
          <div class="px-4 py-2 text-base-content/50">
            {{ $t("Chats.chatScene.edit.targetDesc") }}
          </div>
          <cell-group inset>
            <ChatSceneCharacterTargetItem
              v-for="(character, idx) in targetScene.characterzz"
              :character="character"
              :chat-scene="targetScene"
              class="mb-4"
            />
          </cell-group>
        </Tab>
        <Tab
          :title="$t('Chats.chatScene.edit.event')"
          name="event"
          class="pb-5"
        >
          <div class="px-4 py-2 text-base-content/50">
            {{ $t("Chats.chatScene.edit.eventDesc") }}
          </div>
          <div class="px-4">
            <template v-for="(event, idx) in targetScene.eventzz">
              <ChatSceneEventItem
                :idx="idx"
                :isBottom="idx === targetScene.eventzz!.length - 1"
                :event="event"
                :mate-context="mateContext"
                @update:sort="sortEvent"
                @update:delete="deleteEvent"
              />
            </template>

            <div class="flex justify-end px-4">
              <button
                class="btn btn-sm btn-neutral text-base-content/50 gap-1"
                @click="clickAddEvent"
              >
                <i class="iconify mingcute--add-line text-xs" />
                {{ $t("Buttons.add") }}
              </button>
            </div>
          </div>
        </Tab>
      </Tabs>
    </div>
  </div>
</template>
